import { useParams } from 'react-router-dom';
import { useGetTranscriptSession } from '@utils/api/sessions/use-get-transcript-session.tsx';
import { useGetDocument } from '@utils/api/documents/use-get-document.tsx';
import { AudioProvider } from '@utils/providers/audio-context.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import TranscriptMain from '@components/editors/transcript-editor/transcript-main.tsx';
import { TranscriptProvider } from '@utils/providers/transcript-context.tsx';
import { TranscriptHotkeyProvider } from '@utils/providers/transcript-hotkey-context.tsx';
import { TagAssignmentsProvider } from '@utils/providers/tag-assignments-context.tsx';
import DocumentNotFound from '@components/editors/document-not-found.tsx';
import SessionNotFound from '@components/editors/session-not-found.tsx';
import BlobWarmingOverlay from '@components/editors/blob-warming-overlay.tsx';

type Params = {
  sessionId: string;
  documentId: string;
};

export default function Transcript() {
  const { sessionId, documentId } = useParams<Params>();

  const {
    data: session,
    isLoading: sessionIsLoading,
    error: sessionError,
    isError: sessionIsError
  } = useGetTranscriptSession(sessionId);

  const tier = session?.result?.access_tier;
  const isWarming = tier === 'cold' || tier === 'archive';

  const {
    data: document,
    isLoading: documentIsLoading,
    error: documentError,
    isError: documentIsError
  } = useGetDocument({
    sessionId,
    documentId,
    enabled: !!sessionId && !!documentId && !isWarming
  });

  if (sessionIsLoading || (!isWarming && documentIsLoading)) {
    return (
      <div className="h-screen w-full flex justify-center items-center align-middle">
        <Loader />
      </div>
    );
  }

  if (sessionIsError) {
    return <SessionNotFound error={sessionError} />;
  }

  if (!session || !sessionId) {
    return <SessionNotFound error={sessionError} />;
  }

  if (isWarming) {
    return <BlobWarmingOverlay session={session.result} />;
  }

  if (documentIsError) {
    return <DocumentNotFound error={documentError} />;
  }

  if (!document || !documentId) {
    return <DocumentNotFound error={documentError} />;
  }

  return (
    <AudioProvider timecodeOffset={document.result.timecode_offset}>
      <TranscriptProvider session={session.result} document={document}>
        <TagAssignmentsProvider sessionId={sessionId!} documentId={documentId!}>
          <TranscriptHotkeyProvider>
            <TranscriptMain sessionId={sessionId!} documentId={documentId!} />
          </TranscriptHotkeyProvider>
        </TagAssignmentsProvider>
      </TranscriptProvider>
    </AudioProvider>
  );
}
